/**
 * PanelRefreshButton — Small refresh control for admin panels.
 *
 * Usage (inside a panel's header):
 *   const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
 *   const load = useCallback(async () => {
 *     // ...existing fetch...
 *     setLastRefreshedAt(Date.now());
 *   }, [...]);
 *
 *   <PanelRefreshButton onRefresh={load} lastRefreshedAt={lastRefreshedAt} />
 *
 * Visual:
 *   - 28×28 icon button, spinning icon while loading
 *   - Brief green pulse on success, red pulse + toast on error
 *   - "Last refreshed: Xs ago" micro-timestamp updates every 30s
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

function formatAge(ts) {
  if (!ts) return "never";
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function PanelRefreshButton({ onRefresh, lastRefreshedAt, testId = "panel-refresh-btn" }) {
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(null); // "ok" | "err" | null
  const [, force] = useState(0);
  const pulseTimer = useRef(null);

  // Keep "Xs ago" text live — tick every 30s.
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const handleClick = useCallback(async () => {
    if (loading || !onRefresh) return;
    setLoading(true);
    setPulse(null);
    try {
      await onRefresh();
      setPulse("ok");
    } catch (e) {
      setPulse("err");
      toast.error("Refresh failed — tap to retry", { duration: 3500 });
    } finally {
      setLoading(false);
      clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulse(null), 800);
    }
  }, [loading, onRefresh]);

  const ringColor =
    pulse === "ok" ? "ring-emerald-400/60" :
    pulse === "err" ? "ring-red-400/60" : "";

  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-[10px] text-slate-400 font-medium tabular-nums" data-testid={`${testId}-age`}>
        {formatAge(lastRefreshedAt)}
      </span>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={`w-7 h-7 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition disabled:opacity-50 ring-2 ring-transparent ${ringColor}`}
        title="Refresh this panel"
        aria-label="Refresh panel"
        data-testid={testId}
      >
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}
